/**
 * Die fünf Prüfschritte als Karten — das Herzstück der Übersicht.
 *
 * Jede Karte sagt drei Dinge: was zu tun ist, wie weit es ist, und was
 * der nächste Klick bewirkt. „Prüfung starten" auf einer offenen Karte,
 * „Fortsetzen" auf einer begonnenen, ein grüner Haken auf einer erledigten.
 * Die Reihenfolge ist die der Checkliste; springen darf man trotzdem —
 * ein Beirat, der die Belege zuerst sehen will, soll nicht an Schritt 1
 * scheitern.
 */

import type { Dokument, Kategorie, Schritt } from '../../lib/typen';

export interface SchrittKartenProps {
  schritte: Schritt[];
  kategorien: Kategorie[];
  dokumente: Dokument[];
  /** Zahlen für Schritt 3 aus der Belegprüfung. */
  belege: { gesamt: number; geprueft: number };
  rueckfragenJeSchritt: Record<number, number>;
  onOeffnen: (nummer: number) => void;
  abgeschlossen: boolean;
}

export function SchrittKarten({
  schritte,
  kategorien,
  dokumente,
  belege,
  rueckfragenJeSchritt,
  onOeffnen,
  abgeschlossen,
}: SchrittKartenProps) {
  return (
    <div className="schritte-liste">
      {schritte.map((s) => {
        const kat = kategorien.filter((k) => k.schritt === s.nummer);
        const katIds = new Set(kat.map((k) => k.id));
        const doks = dokumente.filter((d) => katIds.has(d.kategorieId));
        const rueckfragen = rueckfragenJeSchritt[s.nummer] ?? 0;
        const erledigt = s.status === 'erledigt';

        return (
          <div key={s.nummer} className={`card schritt-karte ${s.status}`}>
            <div className={`schritt-nummer ${s.status}`} aria-hidden="true">
              {erledigt ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12l5 5L19 7" />
                </svg>
              ) : (
                s.nummer
              )}
            </div>
            <div className="schritt-text">
              <div className="schritt-titel">
                {s.titel}
                {erledigt && <span className="pill ok"><span className="dot" />Abgeschlossen</span>}
                {s.status === 'laufend' && <span className="pill n"><span className="dot" />Begonnen</span>}
                {rueckfragen > 0 && <span className="badge w">{rueckfragen} Rückfrage{rueckfragen === 1 ? '' : 'n'}</span>}
              </div>
              <p className="s" style={{ marginBottom: 4 }}>{s.beschreibung}</p>
              <p className="s" style={{ marginTop: 0, color: 'var(--tx-3)' }}>Üblich dazu: {s.ueblich}</p>
              <div className="schritt-meta">
                {s.nummer === 3 ? (
                  <span>
                    {belege.geprueft} von {belege.gesamt} Belegen geprüft
                  </span>
                ) : (
                  <span>
                    {doks.length === 0
                      ? 'Noch keine Unterlagen'
                      : `${doks.filter((d) => d.pruefstatus === 'geprueft').length} von ${doks.length} Unterlagen geprüft`}
                  </span>
                )}
                {erledigt && s.erledigtVon && (
                  <span>
                    von {s.erledigtVon}
                    {s.erledigtAm ? ` am ${new Date(s.erledigtAm).toLocaleDateString('de-AT')}` : ''}
                  </span>
                )}
              </div>
            </div>
            <div className="schritt-aktion">
              {erledigt || abgeschlossen ? (
                <button className="btn" onClick={() => onOeffnen(s.nummer)}>
                  Ansehen
                </button>
              ) : s.status === 'laufend' ? (
                <button className="btn primary" onClick={() => onOeffnen(s.nummer)}>
                  Fortsetzen →
                </button>
              ) : (
                <button className="btn primary" onClick={() => onOeffnen(s.nummer)}>
                  Prüfung starten →
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
